/** 계약서 템플릿 데이터 모델 (편집기 내부 표현) */

export type FieldType =
  | "variable" // {{변수}} — 자동/수동 채움 텍스트
  | "text" // 텍스트 입력
  | "longText" // 긴 텍스트 입력
  | "date" // 날짜 입력
  | "checkbox" // 동의 체크박스
  | "signature" // 보호자 서명 (캔버스)
  | "seal" // 사업장 도장 자리
  | "address"; // 주소 입력 (AddressSearch + 상세주소)

export type CheckboxDisplayMode = "MARK_ONLY" | "BOX_AND_MARK";
export type CheckboxSelectionMode = "SINGLE" | "ONE_OF" | "MULTI";
export type TextInputMode = "SINGLE_LINE" | "MULTILINE";

export interface CheckboxFieldOptions {
  checkboxDisplay?: CheckboxDisplayMode;
  checkboxMode?: CheckboxSelectionMode;
  groupKey?: string;
  groupLabel?: string;
  optionValue?: string;
  semanticType?: string;
  normalizedValue?: string;
}

export interface ContractFieldOptions extends CheckboxFieldOptions {
  textMode?: TextInputMode;
}

export interface ContractField {
  id: string;
  type: FieldType;
  key: string; // 본문 마커에 사용 — {{key}} 또는 [[필드:type|key]]
  label: string; // UI 표시명
  auto: boolean; // 자동 채움 여부 (보호자명·펫이름 등)
  required: boolean;
  description?: string; // 부가 설명 (체크박스 동의문 등)
  defaultValue?: string;
  fieldOptions?: ContractFieldOptions;
  /** 렌더된 A4 페이지 위 필드 배치 좌표. 기준은 794×1123 명목 px, 좌상단 원점. */
  pageNo?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/** 계약서 유형 — 보호자 계약 화면 분기/표시용 */
export type ContractTemplateType =
  | "CONTRACT_TYPE_CONSIGNMENT"
  | "CONTRACT_TYPE_CCTV"
  | "CONTRACT_TYPE_VACCINATION"
  | "CONTRACT_TYPE_HEALTH"
  | "CONTRACT_TYPE_CUSTOM";

export interface ContractTemplateData {
  contractSeq?: number; // 신규면 undefined
  name: string;
  body: string; // 본문 — 마커 포함 텍스트
  fields: ContractField[];
  active: boolean;
  type: ContractTemplateType;
  /** 첫 예약 시 자동 발송 양식 여부 — 사업장당 Y 1개. 그 외는 사장님이 수동 발송 시 선택. */
  autoFirstReservYn: "Y" | "N";
  /** 보호자에게 보낼 때 사용할 자동 변수 후보 (사업자가 펫·고객 선택 시 자동 채워짐) */
  autoVariables?: string[];
}

export const EMPTY_TEMPLATE: ContractTemplateData = {
  name: "",
  body: "",
  fields: [],
  active: true,
  type: "CONTRACT_TYPE_CONSIGNMENT",
  autoFirstReservYn: "N",
};

/** 자동 채움 가능한 변수 (DB에서 즉시 가져올 수 있는 값) — 보호자 ContractText 와 동일한 표기.
 *  ※ 서명/도장은 변수가 아닌 "필드(서명·도장)" 으로만 삽입 — 중복 방지 */
export const AUTO_VARIABLES = [
  { key: "guardianName", label: "보호자 이름", source: "user.userName" },
  { key: "guardianNickname", label: "보호자 닉네임", source: "user.nickname" },
  { key: "guardianEmail", label: "보호자 이메일", source: "user.email" },
  { key: "guardianPhone", label: "보호자 전화번호", source: "user.phoneNumber" },
  { key: "guardianAddress", label: "보호자 주소", source: "user.address" },
  { key: "petName", label: "반려동물 이름", source: "pet.petName" },
  { key: "petBirthday", label: "생년월일", source: "pet.petBirthday" },
  { key: "petBreed", label: "품종", source: "pet.petBreed" },
  { key: "petWeight", label: "몸무게", source: "pet.petWeight" },
  { key: "petNeutered", label: "중성화 여부", source: "pet.neuter" },
  { key: "petVaccinations", label: "1년내 접종 정보", source: "pet.inoculation" },
  { key: "petPersonality", label: "성격/사회성", source: "pet.personality" },
  { key: "petAllergies", label: "알레르기 정보", source: "pet.allergy" },
  { key: "petOther", label: "기타 특이사항", source: "pet.memo" },
  { key: "companyName", label: "사업장명", source: "company.companyName" },
  { key: "businessNumber", label: "사업자등록번호", source: "company.businessNumber" },
  { key: "companyAddress", label: "사업장 주소", source: "company.companyAddr+detail" },
  { key: "ceoName", label: "대표자 이름", source: "company.ceoSeq→bizUser.bizUserName" },
  { key: "companyPhone", label: "사업장 전화번호", source: "company.mainTelNumber" },
  { key: "contractDate", label: "계약 일자", source: "system.today" },
];

/** 텍스트 영역 커서 위치에 문자열 삽입 */
export const insertAtCursor = (
  textarea: HTMLTextAreaElement | null,
  insert: string
): string | null => {
  if (!textarea) return null;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = textarea.value.substring(0, start);
  const after = textarea.value.substring(end);
  const newValue = before + insert + after;

  // 커서 위치를 삽입한 텍스트 끝으로 이동 (다음 렌더 후)
  setTimeout(() => {
    if (textarea) {
      textarea.focus();
      textarea.setSelectionRange(start + insert.length, start + insert.length);
    }
  }, 0);

  return newValue;
};

/** 마커 표기법: [variableName] (단일 대괄호 + camelCase) — 보호자 ContractText 와 동일 */
const VAR_REGEX = /\[([a-zA-Z][a-zA-Z0-9_]*)\]/g;
/** 레거시 표기: {{변수}} — 기존 DB 에 저장된 본문 호환용 (한글 키 포함) */
const LEGACY_VAR_REGEX = /\{\{([^}]+)\}\}/g;
/** [[필드:type|key|label]] — 사업자 추가 입력 필드 (체크박스/서명/도장 등) */
const FIELD_REGEX = /\[\[필드:([^|]+)\|([^|\]]+)(?:\|([^\]]+))?\]\]/g;

/** 레거시 한글 키 → 신규 영문 키 매핑 (이미 저장된 본문 호환) */
const LEGACY_KEY_MAP: Record<string, string> = {
  보호자명: "guardianName",
  보호자: "guardianName",
  닉네임: "guardianNickname",
  이메일: "guardianEmail",
  전화번호: "guardianPhone",
  보호자연락처: "guardianPhone",
  주소: "guardianAddress",
  펫이름: "petName",
  반려동물명: "petName",
  품종: "petBreed",
  견종: "petBreed",
  체중: "petWeight",
  성별: "petNeutered",
  나이: "petBirthday",
  생년월일: "petBirthday",
  특이사항: "petOther",
  알러지: "petAllergies",
  알레르기: "petAllergies",
  접종: "petVaccinations",
  사업장명: "companyName",
  상호: "companyName",
  업체명: "companyName",
  대표자명: "ceoName",
  대표자: "ceoName",
  사업자번호: "businessNumber",
  계약일: "contractDate",
  작성일: "contractDate",
  // 시작일/종료일(계약기간)·위탁료는 자동변수 아님 — 본문 직접 입력(빈칸 유지). 매핑하지 않음.
};

/** 본문에서 마커 추출 (미리보기·검증용) */
export const parseMarkers = (body: string) => {
  const variables: string[] = [];
  const fields: { type: string; key: string; label?: string }[] = [];

  let match: RegExpExecArray | null;
  // [필드:...] 매칭이 [variable] 와 충돌하지 않도록 필드부터 제거 후 변수 추출
  const stripped = body.replace(FIELD_REGEX, "");
  VAR_REGEX.lastIndex = 0;
  while ((match = VAR_REGEX.exec(stripped)) !== null) {
    if (!variables.includes(match[1])) variables.push(match[1]);
  }
  // 레거시 {{변수}} 도 함께 추출
  LEGACY_VAR_REGEX.lastIndex = 0;
  while ((match = LEGACY_VAR_REGEX.exec(stripped)) !== null) {
    const k = match[1].trim();
    const mapped = LEGACY_KEY_MAP[k] || k;
    if (!variables.includes(mapped)) variables.push(mapped);
  }

  FIELD_REGEX.lastIndex = 0;
  while ((match = FIELD_REGEX.exec(body)) !== null) {
    fields.push({
      type: match[1],
      key: match[2],
      label: match[3],
    });
  }

  return { variables, fields };
};

/** 마커를 사람이 읽기 좋은 HTML 로 변환 (미리보기) */
export const renderPreviewHtml = (
  template: ContractTemplateData,
  values: Record<string, string> = {}
): string => {
  let html = template.body;

  // [[필드:type|key|label]] 치환 (변수 치환보다 먼저 — 패턴 충돌 방지)
  html = html.replace(
    FIELD_REGEX,
    (_, type: string, key: string, label?: string) => {
      // 라벨 우선 → 없으면 KEY_TO_LABEL → 없으면 필드 타입 기본 한글
      const labelText = label && label.trim()
        ? label
        : englishToKorean(key) !== key
          ? englishToKorean(key)
          : DEFAULT_FIELD_LABEL[type] || key;
      // 서명/도장은 실제 이미지가 values 에 들어오면 그걸 바로 박아서 "실제로 서명된 화면" 처럼 보이게
      if (type === "signature" && values.signatureFileUrl) {
        return `<img src="${values.signatureFileUrl}" alt="서명" class="inline-block max-h-12 align-middle" />`;
      }
      if (type === "seal" && values.sealImageUrl) {
        return `<img src="${values.sealImageUrl}" alt="도장" class="inline-block max-h-14 align-middle mx-1" />`;
      }
      // 보호자가 동적 입력 필드를 채웠으면 본문에 즉시 반영 (체크박스 ✅, 텍스트/날짜 값)
      const v = values[key];
      if (type === "checkbox") {
        return v
          ? `<span class="text-[#22A55B] font-semibold">✅ ${escapeHtml(labelText)}</span>`
          : `<span class="text-[#999]">☐ ${escapeHtml(labelText)}</span>`;
      }
      if ((type === "text" || type === "date") && v) {
        return `<span class="text-[#22A55B] font-semibold underline">${escapeHtml(String(v))}</span>`;
      }
      return renderFieldPreview(type, labelText);
    }
  );

  // [variableName] 치환
  html = html.replace(VAR_REGEX, (_full, key: string) => substituteVar(values, key));

  // 레거시 {{변수}} 치환 — 기존 DB 본문 호환 (한글 키도 영문 키로 매핑)
  html = html.replace(LEGACY_VAR_REGEX, (_full, raw: string) => {
    const k = raw.trim();
    const mapped = LEGACY_KEY_MAP[k] || k;
    return substituteVar(values, mapped);
  });

  // 페이지 마커 [[PAGE:N]] → 시각적 구분선 + 페이지 번호 라벨
  html = html.replace(/\[\[PAGE:(\d+)\]\]/g, (_full, num: string) => {
    const n = parseInt(num, 10);
    if (n <= 1) {
      // 첫 페이지는 헤더만 살짝
      return `<div data-page="${n}" style="position:relative;padding-top:4pt;">
        <span style="position:absolute;top:0;right:0;font-size:9pt;color:#AAA;font-family:monospace;">p. ${n}</span>
      </div>`;
    }
    return `<div data-page="${n}" style="position:relative;border-top:1px dashed #BBB;margin-top:14pt;padding-top:14pt;">
      <span style="position:absolute;top:6pt;right:0;font-size:9pt;color:#AAA;font-family:monospace;">p. ${n}</span>
    </div>`;
  });

  // 줄바꿈
  html = html.replace(/\n/g, "<br/>");

  return html;
};

/** 필드 타입별 기본 한글 라벨 (라벨/키 둘 다 누락된 경우 fallback) */
const DEFAULT_FIELD_LABEL: Record<string, string> = {
  signature: "보호자 서명",
  seal: "사업장 도장",
  checkbox: "동의",
  text: "직접 입력",
  date: "날짜",
  address: "주소 입력",
};

/** 마커 → 한글 라벨 매핑 (값이 없을 때 placeholder 표시용)
 *  영문 키는 데이터 매칭에만 사용 — 화면에는 모두 한글 라벨로 노출 */
const KEY_TO_LABEL: Record<string, string> = {
  // 자동 변수
  guardianName: "보호자 이름",
  guardianNickname: "보호자 닉네임",
  guardianEmail: "보호자 이메일",
  guardianPhone: "보호자 전화번호",
  guardianAddress: "보호자 주소",
  petName: "반려동물 이름",
  petBirthday: "생년월일",
  petBreed: "품종",
  petWeight: "몸무게",
  petNeutered: "중성화 여부",
  petVaccinations: "접종 정보",
  petPersonality: "성격",
  petAllergies: "알레르기",
  petOther: "기타 특이사항",
  companyName: "수탁업체명",
  businessNumber: "사업자등록번호",
  contractDate: "계약 일자",
  // 수동 변수
  startDate: "시작일",
  endDate: "종료일",
  fee: "위탁료",
  notes: "특이사항",
  // 자주 등장하는 필드 키 (구버전 호환)
  guardianSign: "보호자 서명",
  companySeal: "사업장 도장",
  consent: "동의",
  memo: "메모",
  fillDate: "작성일",
  signatureFileUrl: "보호자 서명",
};

/** 영문 키 → 한글 표시명 (KEY_TO_LABEL 에 없으면 자동 변환 시도) */
const englishToKorean = (key: string): string => {
  if (KEY_TO_LABEL[key]) return KEY_TO_LABEL[key];
  // 한글이 이미 포함된 키는 그대로 (사장님이 직접 만든 한글 키)
  if (/[ㄱ-힝]/.test(key)) return key;
  // camelCase 영문 → 사람이 읽을 수 있는 한국어 라벨 생성 시도
  // guardianName → "보호자 이름" 처럼 변환할 수 없으므로 fallback 으로 첫 글자 대문자 + 공백 분리
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
};

const substituteVar = (values: Record<string, string>, key: string): string => {
  const v = values[key];
  // 레거시 호환 — 기존 본문이 [signatureFileUrl] 마커를 쓰고 있으면 이미지로 박음
  if (key === "signatureFileUrl") {
    return v
      ? `<img src="${v}" alt="서명" class="inline-block max-h-12 align-middle" />`
      : `<span class="bg-[#FFF7E6] text-[#FF9934] font-semibold rounded px-1">보호자 서명</span>`;
  }
  // 값이 채워졌으면 색깔만 — [] 표시 없이 강조 색으로 노출
  if (v) return `<span class="text-[#22A55B] font-semibold">${escapeHtml(v)}</span>`;
  // 미입력 변수도 [] 없이 — 한글 라벨 + 강조 색 (영문 키 노출 방지)
  const label = englishToKorean(key);
  return `<span class="bg-[#FFF7E6] text-[#FF9934] font-semibold rounded px-1">${escapeHtml(label)}</span>`;
};

const renderFieldPreview = (type: string, label: string): string => {
  const map: Record<string, { icon: string; color: string }> = {
    text: { icon: "🔤", color: "#7D6DFF" },
    date: { icon: "📅", color: "#FF9934" },
    checkbox: { icon: "☑️", color: "#22A55B" },
    signature: { icon: "✏️", color: "#F472B6" },
    seal: { icon: "🖋", color: "#C8102E" },
    address: { icon: "📍", color: "#0EA5E9" },
  };
  const { icon = "🔵", color = "#999" } = map[type] || {};
  return `<span class="inline-block bg-white border-2 border-dashed rounded px-2 py-0.5 mx-0.5" style="border-color:${color}; color:${color}; font-weight:600; font-size: 12px;">${icon} ${escapeHtml(
    label
  )}</span>`;
};

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/** 새 필드 ID 생성 */
export const generateFieldId = () =>
  `f${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
